import React, { ChangeEvent, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import SearchBar from "../../components/SearchBar";
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    IconButton,
    Button,
    Tabs,
    Tab,
    Box,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import AddEquipment from "../../Modals/addEquipmentModal";
import AddMuscleModal from "../../Modals/addMuscleModal";
import { useGetSettings, useCreateSetting, useUpdateSetting, useDeleteSetting } from "../../api/hooks/useSettings";
import { WORKOUT_ATTRIBUTES } from "../../constants/settings";
import type { SettingItem } from "../../api/types/settings.types";
import { showError, showSuccess } from "../../helpers/toast";
import ConfirmationDialog from "../../components/ConfirmationDialog";
import Pagination from "../../components/Panigation";

function CustomTabPanel(props: any) {
    const { children, value, index, ...other } = props;

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            {...other}
        >
            {value === index && <>{children}</>}
        </div>
    );
}

function a11yProps(index: number) {
    return {
        id: `simple-tab-${index}`,
        "aria-controls": `simple-tabpanel-${index}`,
    };
}

const ManageSettings = () => {
    const navigate = useNavigate();

    const [valueTabs, setValueTabs] = useState(0);
    const handleChangeTabs = (event: any, newValue: number) => {
        setValueTabs(newValue);
        setSearchTerm("");
        setPage(1);
    };

    const [searchTerm, setSearchTerm] = useState("");
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
    const [page, setPage] = useState(1);
    const [limit] = useState(10);

    const [openEquipment, setOpenEquipment] = useState(false);
    const [openMuscle, setOpenMuscle] = useState(false);
    const [modalTitle, setModalTitle] = useState("Add");
    const [activeItem, setActiveItem] = useState<SettingItem | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [settingToDelete, setSettingToDelete] = useState<SettingItem | null>(null);

    const TAB_CONFIG = [
        { label: "Equipment", type: WORKOUT_ATTRIBUTES.EQUIPMENT, addLabel: "Add Equipment", modal: "equipment" as const },
        { label: "Muscle Group", type: WORKOUT_ATTRIBUTES.MUSCLE_GROUP, addLabel: "Add Muscle Group", modal: "muscle" as const },
        { label: "Workout Type", type: WORKOUT_ATTRIBUTES.TRAINING_STYLE, addLabel: "Add Workout Type", modal: "muscle" as const },
        { label: "Goal", type: WORKOUT_ATTRIBUTES.GOAL_OUTCOME, addLabel: "Add Goal", modal: "muscle" as const },
    ];

    const currentConfig = TAB_CONFIG[valueTabs] || TAB_CONFIG[0];

    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearchTerm(searchTerm.trim());
            setPage(1);
        }, 400);
        return () => clearTimeout(timer);
    }, [searchTerm, valueTabs]);

    const { data: settingsData, isLoading, error: settingsError } = useGetSettings(
        {
            type: currentConfig.type,
            search: debouncedSearchTerm || undefined,
            page,
            limit,
            offset: (page - 1) * limit,
        },
        { enabled: true }
    );

    const { settingsList, totalCount } = useMemo(() => {
        const raw = settingsData?.data;
        let list: SettingItem[] = [];
        let total = 0;
        if (Array.isArray(raw)) {
            list = raw;
            total = raw.length;
        } else if (raw && typeof raw === "object") {
            if (Array.isArray((raw as any).list)) {
                list = (raw as any).list;
                total = (raw as any).totalCount || list.length;
            }
        }
        return {
            settingsList: Array.isArray(list) ? list : [],
            totalCount: total || 0,
        };
    }, [settingsData]);

    useEffect(() => {
        if (settingsError) {
            showError((settingsError as any)?.response?.data?.message || "Failed to load settings");
        }
    }, [settingsError]);

    const { mutateAsync: createSetting } = useCreateSetting();
    const { mutateAsync: updateSetting } = useUpdateSetting();
    const { mutateAsync: deleteSetting } = useDeleteSetting();

    const openAddModal = () => {
        setActiveItem(null);
        setModalTitle(currentConfig.addLabel);
        currentConfig.modal === "equipment" ? setOpenEquipment(true) : setOpenMuscle(true);
    };

    const openEditModal = (item: SettingItem) => {
        setActiveItem(item);
        setModalTitle(currentConfig.addLabel.replace("Add", "Edit"));
        currentConfig.modal === "equipment" ? setOpenEquipment(true) : setOpenMuscle(true);
    };

    const handleDelete = async (item: SettingItem) => {
        setSettingToDelete(item);
        setDeleteDialogOpen(true);
    };

    const confirmDelete = async () => {
        if (!settingToDelete) return;
        try {
            await deleteSetting(settingToDelete._id);
            showSuccess("Deleted successfully");
        } catch (err: any) {
            showError(err?.response?.data?.message || "Failed to delete");
        } finally {
            setDeleteDialogOpen(false);
            setSettingToDelete(null);
        }
    };

    const cancelDelete = () => {
        setDeleteDialogOpen(false);
        setSettingToDelete(null);
    };

    const handleMuscleSubmit = async (name: string) => {
        const trimmedName = name.trim();
        if (!trimmedName) {
            showError("Name is required and cannot be empty");
            return;
        }

        setIsSubmitting(true);
        try {
            if (activeItem?._id) {
                await updateSetting({ id: activeItem._id, data: { name: trimmedName, type: activeItem.type ?? currentConfig.type } });
                showSuccess("Updated successfully");
            } else {
                await createSetting({ name: trimmedName, type: currentConfig.type });
                showSuccess("Created successfully");
            }
        } catch (err: any) {
            showError(err?.response?.data?.message || "Failed to save");
            throw err;
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleEquipmentSubmit = async ({ name, image }: { name: string; image?: string }) => {
        const trimmedName = name.trim();
        if (!trimmedName) {
            showError("Name is required and cannot be empty");
            return;
        }

        setIsSubmitting(true);
        try {
            if (activeItem?._id) {
                await updateSetting({
                    id: activeItem._id,
                    data: { name: trimmedName, image: image || activeItem.image, type: activeItem.type ?? currentConfig.type },
                });
                showSuccess("Updated successfully");
            } else {
                await createSetting({ name: trimmedName, image, type: currentConfig.type });
                showSuccess("Created successfully");
            }
        } catch (err: any) {
            showError(err?.response?.data?.message || "Failed to save");
            throw err;
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <>
            {/* Title */}
            <div className="main_title">
                <h1>Settings</h1>
                <p>
                    <button onClick={() => navigate("/dashboard")}>Dashboard</button> -
                    Settings
                </p>
            </div>

            {/* Tabs */}
            <div className="table_header">
                <div className="left_s">
                    <Box className="custom_tabs1" sx={{ width: "100%" }}>
                        <Tabs
                            value={valueTabs}
                            onChange={handleChangeTabs}
                            className="custom_tabs_links"
                        >
                            <Tab label="Equipment" {...a11yProps(0)} />
                            <Tab label="Muscle Group" {...a11yProps(1)} />
                            <Tab label="Workout Type" {...a11yProps(2)} />
                            <Tab label="Goal" {...a11yProps(3)} />
                        </Tabs>
                    </Box>
                </div>
            </div>

            <div className="cards">

                {/* -------------------------- EQUIPMENTS TAB ----------------------- */}
                <CustomTabPanel value={valueTabs} index={0}>
                    <div className="table_header">
                        <div className="left_s">
                            <SearchBar
                                value={searchTerm}
                                onChange={(e: ChangeEvent<HTMLInputElement>) => setSearchTerm(e.target.value)}
                                onCross={() => setSearchTerm("")}
                                searchTerm={searchTerm}
                                setDebouncedSearchTerm={setDebouncedSearchTerm}
                                placeholder="Search Equipment..."
                            />
                        </div>

                        <div className="right_s">
                            <Button
                                className="btn btn_primary"
                                onClick={openAddModal}
                            >
                                <AddIcon /> Add Equipment
                            </Button>
                        </div>
                    </div>

                    <TableContainer className="table_container">
                        <Table>
                            <TableHead>
                                <TableRow hover>
                                    <TableCell>Sr.no</TableCell>
                                    <TableCell>Equipment Name</TableCell>
                                    <TableCell>Created Date</TableCell>
                                    <TableCell>Action</TableCell>
                                </TableRow>
                            </TableHead>

                            <TableBody>
                                {isLoading ? (
                                    <TableRow>
                                        <TableCell colSpan={4}>Loading...</TableCell>
                                    </TableRow>
                                ) : settingsList.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={4}>No records found</TableCell>
                                    </TableRow>
                                ) : (
                                    settingsList.map((item, index) => (
                                        <TableRow hover key={item._id}>
                                            <TableCell>{(page - 1) * limit + index + 1}</TableCell>
                                            <TableCell>{item.name || "N/A"}</TableCell>
                                            <TableCell>{item.createdAt ? new Date(item.createdAt).toLocaleDateString() : "N/A"}</TableCell>
                                            <TableCell>
                                                <div className="table_actions v2">
                                                    <IconButton onClick={() => openEditModal(item)}>
                                                        <img src="/static/images/edit2_icon.svg" alt="" />
                                                    </IconButton>
                                                    <IconButton onClick={() => handleDelete(item)}>
                                                        <img src="/static/images/trash2_icon.svg" alt="" />
                                                    </IconButton>
                                                </div>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </CustomTabPanel>

                {/* ----------------------- MUSCLE GROUP TAB ------------------------ */}
                <CustomTabPanel value={valueTabs} index={1}>
                    <div className="table_header">
                        <div className="left_s">
                            <SearchBar
                                value={searchTerm}
                                onChange={(e: ChangeEvent<HTMLInputElement>) => setSearchTerm(e.target.value)}
                                onCross={() => setSearchTerm("")}
                                searchTerm={searchTerm}
                                setDebouncedSearchTerm={setDebouncedSearchTerm}
                                placeholder="Search Muscle Group..."
                            />
                        </div>

                        <div className="right_s">
                            <Button
                                className="btn btn_primary"
                                onClick={openAddModal}
                            >
                                <AddIcon /> Add Muscle Group
                            </Button>
                        </div>
                    </div>

                    <TableContainer className="table_container">
                        <Table>
                            <TableHead>
                                <TableRow hover>
                                    <TableCell>Sr.no</TableCell>
                                    <TableCell>Muscle Group</TableCell>
                                    <TableCell>Created Date</TableCell>
                                    <TableCell>Action</TableCell>
                                </TableRow>
                            </TableHead>

                            <TableBody>
                                {isLoading ? (
                                    <TableRow>
                                        <TableCell colSpan={4}>Loading...</TableCell>
                                    </TableRow>
                                ) : settingsList.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={4}>No records found</TableCell>
                                    </TableRow>
                                ) : (
                                    settingsList.map((item, index) => (
                                        <TableRow hover key={item._id}>
                                            <TableCell>{(page - 1) * limit + index + 1}</TableCell>
                                            <TableCell>{item.name || "N/A"}</TableCell>
                                            <TableCell>{item.createdAt ? new Date(item.createdAt).toLocaleDateString() : "N/A"}</TableCell>
                                            <TableCell>
                                                <div className="table_actions v2">
                                                    <IconButton onClick={() => openEditModal(item)}>
                                                        <img src="/static/images/edit2_icon.svg" alt="" />
                                                    </IconButton>
                                                    <IconButton onClick={() => handleDelete(item)}>
                                                        <img src="/static/images/trash2_icon.svg" alt="" />
                                                    </IconButton>
                                                </div>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </CustomTabPanel>

                {/* ----------------------- TRAINING STYLE TAB ------------------------ */}
                <CustomTabPanel value={valueTabs} index={2}>
                    <div className="table_header">
                        <div className="left_s">
                            <SearchBar
                                value={searchTerm}
                                onChange={(e: ChangeEvent<HTMLInputElement>) => setSearchTerm(e.target.value)}
                                onCross={() => setSearchTerm("")}
                                searchTerm={searchTerm}
                                setDebouncedSearchTerm={setDebouncedSearchTerm}
                                placeholder="Search Workout Type..."
                            />
                        </div>

                        <div className="right_s">
                            <Button
                                className="btn btn_primary"
                                onClick={openAddModal}
                            >
                                <AddIcon /> Add Workout Type
                            </Button>
                        </div>
                    </div>

                    <TableContainer className="table_container">
                        <Table>
                            <TableHead>
                                <TableRow hover>
                                    <TableCell>Sr.no</TableCell>
                                    <TableCell>Workout Type</TableCell>
                                    <TableCell>Created Date</TableCell>
                                    <TableCell>Action</TableCell>
                                </TableRow>
                            </TableHead>

                            <TableBody>
                                {isLoading ? (
                                    <TableRow>
                                        <TableCell colSpan={4}>Loading...</TableCell>
                                    </TableRow>
                                ) : settingsList.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={4}>No records found</TableCell>
                                    </TableRow>
                                ) : (
                                    settingsList.map((item, index) => (
                                        <TableRow hover key={item._id}>
                                            <TableCell>{(page - 1) * limit + index + 1}</TableCell>
                                            <TableCell>{item.name || "N/A"}</TableCell>
                                            <TableCell>{item.createdAt ? new Date(item.createdAt).toLocaleDateString() : "N/A"}</TableCell>
                                            <TableCell>
                                                <div className="table_actions v2">
                                                    <IconButton onClick={() => openEditModal(item)}>
                                                        <img src="/static/images/edit2_icon.svg" alt="" />
                                                    </IconButton>
                                                    <IconButton onClick={() => handleDelete(item)}>
                                                        <img src="/static/images/trash2_icon.svg" alt="" />
                                                    </IconButton>
                                                </div>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </CustomTabPanel>

                {/* ----------------------- GOAL OUTCOME TAB ------------------------ */}
                <CustomTabPanel value={valueTabs} index={3}>
                    <div className="table_header">
                        <div className="left_s">
                            <SearchBar
                                value={searchTerm}
                                onChange={(e: ChangeEvent<HTMLInputElement>) => setSearchTerm(e.target.value)}
                                onCross={() => setSearchTerm("")}
                                searchTerm={searchTerm}
                                setDebouncedSearchTerm={setDebouncedSearchTerm}
                                placeholder="Search Goal..."
                            />
                        </div>

                        <div className="right_s">
                            <Button
                                className="btn btn_primary"
                                onClick={openAddModal}
                            >
                                <AddIcon /> Add Goal
                            </Button>
                        </div>
                    </div>

                    <TableContainer className="table_container">
                        <Table>
                            <TableHead>
                                <TableRow hover>
                                    <TableCell>Sr.no</TableCell>
                                    <TableCell>Goal</TableCell>
                                    <TableCell>Created Date</TableCell>
                                    <TableCell>Action</TableCell>
                                </TableRow>
                            </TableHead>

                            <TableBody>
                                {isLoading ? (
                                    <TableRow>
                                        <TableCell colSpan={4}>Loading...</TableCell>
                                    </TableRow>
                                ) : settingsList.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={4}>No records found</TableCell>
                                    </TableRow>
                                ) : (
                                    settingsList.map((item, index) => (
                                        <TableRow hover key={item._id}>
                                            <TableCell>{(page - 1) * limit + index + 1}</TableCell>
                                            <TableCell>{item.name || "N/A"}</TableCell>
                                            <TableCell>{item.createdAt ? new Date(item.createdAt).toLocaleDateString() : "N/A"}</TableCell>
                                            <TableCell>
                                                <div className="table_actions v2">
                                                    <IconButton onClick={() => openEditModal(item)}>
                                                        <img src="/static/images/edit2_icon.svg" alt="" />
                                                    </IconButton>
                                                    <IconButton onClick={() => handleDelete(item)}>
                                                        <img src="/static/images/trash2_icon.svg" alt="" />
                                                    </IconButton>
                                                </div>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </CustomTabPanel>
                
                {/* Shared pagination for all tabs */}
                {!isLoading && !settingsError && totalCount > 0 && (
                    <Pagination
                        module={settingsList}
                        page={page}
                        onPageChange={(newPage) => {
                            setPage(newPage);
                            window.scrollTo({ top: 0, behavior: "smooth" });
                        }}
                        totalPages={Math.ceil(totalCount / limit)}
                        totalCount={totalCount}
                        setPage={setPage}
                        limit={limit}
                    />
                )}

            </div>

            {/* Modals */}
            <AddEquipment
                open={openEquipment}
                onClose={() => setOpenEquipment(false)}
                setOpen={setOpenEquipment}
                // @ts-ignore allow dynamic title for heading
                title={modalTitle}
                initialName={activeItem?.name}
                initialImage={activeItem?.image}
                onSubmit={handleEquipmentSubmit}
                isSubmitting={isSubmitting}
            />

            <AddMuscleModal
                open={openMuscle}
                onClose={() => setOpenMuscle(false)}
                setOpen={setOpenMuscle}
                // @ts-ignore allow dynamic title for heading
                title={modalTitle}
                initialName={activeItem?.name}
                onSubmit={handleMuscleSubmit}
                isSubmitting={isSubmitting}
            />

            <ConfirmationDialog
                open={deleteDialogOpen}
                onClose={cancelDelete}
                onConfirm={confirmDelete}
                title="Delete Item"
                message="Are you sure you want to delete this item? This action cannot be undone."
                confirmText="Delete"
                cancelText="Cancel"
            />

        </>
    );
};

export default ManageSettings;
